import React from "react";
import { useLanguage } from '../context/LanguageContext';

// Import an icon for the placeholder, for example from react-icons
// If you don't have it, run: npm install react-icons
import { FaRegImage } from 'react-icons/fa';

const About = () => {
  const { currentLanguage, getText, LANGUAGES } = useLanguage();

  // Content data separated by language
  const aboutContent = {
    banner: {
      title: {
        [LANGUAGES.EN]: 'About Mahāvihāra University',
        [LANGUAGES.MM]: 'မဟာဝိဟာရတက္ကသိုလ်အကြောင်း'
      },
      subtitle: {
        [LANGUAGES.EN]: 'Tradition & Modernity in Buddhist Education',
        [LANGUAGES.MM]: 'ဗုဒ္ဓပညာရေးတွင် ရိုးရာနှင့် ခေတ်မီမှု'
      }
    },

    officialDoctrine: {
      title: {
        [LANGUAGES.EN]: 'Official Doctrine',
        [LANGUAGES.MM]: 'တက္ကသိုလ်၏တရားဝင်ပြဌာန်းထားသောမူ'
      },
      content: {
        [LANGUAGES.EN]: `The official doctrine of this university is:\n\n"The disciplinary rules (Sikkhāpada) from the Vinaya Pitaka must be thoroughly and completely observed."\n\n"The university's guiding principle: Live and Practice in Buddhist traditional Ways, learn and teach in Modern Methods"`,
        [LANGUAGES.MM]: `ဤတက္ကသိုလ်၏တရားဝင်ပြဌာန်းထားသောမူတစ်ခုမှာ\n\n"ဝိနည်းပိဋကတ်၌လာသောသိက္ခာပုဒ်တော်များကို ထက်ဝန်းကျင်ပြည့်စုံစွာ ဖြည့်ကျင့်ရမည်"\n\n"နေနည်းရှေးကျ၊သင်နည်းခေတ်မီ" ဟူသောတက္ကသိုလ်၏လမ်းညွှန်မူဝါဒကိုအစဉ်ဂရုပြုရမည်။`
      }
    },

    guidingPrinciple: {
      title: {
        [LANGUAGES.EN]: 'Guiding Principle',
        [LANGUAGES.MM]: '"နေနည်းရှေးကျ၊သင်နည်းခေတ်မီ"'
      },
      content: {
        [LANGUAGES.EN]: `The admonishing "Live and Practice in Buddhist traditional Ways, learn and teach in Modern Methods" carries a meaning so profound that it could fill an entire book with its depth.\n\nIn summary:\n\n"Live and Practice in Buddhist traditional Ways" means a simple way of life that cherishes the ancient traditions of the Sangha, which consists of noble monks who uphold the Vinaya (monastic discipline) and the Dhamma (teachings) perfectly.\n\nModern teaching methods are, in fact, a continuation of ancient teaching methods from the Inwa period, represented by the phrase "သု စိ ပု ဘာ ဝိ လိသိ ဓာ".`,
        [LANGUAGES.MM]: `ဤ"နေနည်းရှေးကျသင်နည်းခေတ်မီ" ဟူသောဩဝါဒတော်သည် အနက်အဓိပ္ပါယ်အားဖြင့် ကျမ်း တစ်စောင်ပေတစ်ဖွဲ့ရေးရလောက်အောင်ကျယ်ဝန်းလှပေသည်။\n\nအကျဉ်းချုပ်မှာ အောက်ပါအတိုင်းဖြစ်သည်။\n\nနေနည်းရှေးကျ ဆိုသည်မှာ ဝိနည်းမာမက၊ဓမ္မမာမက ဖြစ်တော်မူကြသော အရှင်ကောင်းတို့၏သာသနာ့ ရှေးရိုးလမ်းစဉ်ကိုမြတ်နိုးစွာ လိုက်နာသောရိုးရှင်းသောဘဝနေနည်း ပင်ဖြစ်သည်။\n\nသင်နည်းခေတ်မီ ဆိုသည်မှာ "သု စိ ပု ဘာ ဝိ လိသိ ဓာ" ဟူသောအင်းဝခေတ် ရှေးမထေမြတ်တို့၏သင် နည်းပင်ဖြစ်သည်။`
      }
    },

    motto: {
      title: {
        [LANGUAGES.EN]: 'University Motto',
        [LANGUAGES.MM]: 'မဟာဝိဟာရဓမ္မဝိနယတက္ကသိုလ်၏ဆောင်ပုဒ်'
      },
      content: {
        [LANGUAGES.EN]: `The Motto of Mahāvihāra Dhamma Vinaya University is "Sīlaparidhotā paÒÒā, paÒÒāparidhotam Sīlam."\n\n"Wisdom is purified by morality, and morality is purified by wisdom. In other words, morality purifies wisdom, and wisdom purifies morality."\n\nThis concept is a central tenet of many philosophical and religious traditions, including Buddhism. It emphasizes the idea that intellectual understanding (wisdom) is not enough for a fulfilling life; one must also cultivate moral character (morality). Similarly, moral behavior without understanding (wisdom) can be misguided or incomplete.`,
        [LANGUAGES.MM]: `မဟာဝိဟာရဓမ္မဝိနယတက္ကသိုလ်၏ဆောင်ပုဒ်မှာ "သီလပရိဓောတာ ပညာ၊ ပညာ ပရိဓောတံ သီလံ" ဟူ၍ဖြစ်သည်။\n\n"ပညာကိုသီလဖြင့်ထက်ဝန်းကျင်ဆေးကြောသုတ်သင်အပ်၏။ သီလကိုပညာဖြင့်ထက်ဝန်းကျင်ဆေးကြော သုတ်သင်အပ်၏။ တစ်နည်းပညာကိုသီလသည်ဖြူစင်စေအပ်သည်။ (ဝါ) ပညာသည် သီလကြောင့်ဖြူစင်၏။ သီလကိုပညာသည်ဖြူစင်စေအပ်၏။ (ဝါ) သီလသည် ပညာကြောင့်ဖြူစင်၏။\n\nဤအယူအဆသည် ဗုဒ္ဓဘာသာအပါအဝင် ဒဿနိကနှင့် ဘာသာရေးအစဉ်အလာများ၏ အဓိကအခြေခံအယူအဆဖြစ်သည်။`
      }
    },
  
    // Reworked history section for timeline display
    history: {
      title: {
        [LANGUAGES.EN]: 'Background History',
        [LANGUAGES.MM]: 'နောက်ခံသမိုင်းကြောင်း'
      },
      timeline: [
        {
          date: { [LANGUAGES.EN]: 'Ancient Roots', [LANGUAGES.MM]: 'ရှေးခေတ် အရင်းအမြစ်' },
          title: { [LANGUAGES.EN]: 'Sihhala Mahavihara', [LANGUAGES.MM]: 'သီဟိုဠ်မဟာဝိဟာရကျောင်းတိုက်ကြီး' },
          text: { [LANGUAGES.EN]: 'King Devanampiya Tissa donated the Meghavana Garden to the venerable Mahinda, establishing the great Mahavihara monastery where 3,000 monks resided. The teachings flourished unhindered until the reign of King Vattagamani, 440 years into the Buddhist era.', [LANGUAGES.MM]: 'ဒေဝါနံပိယတိဿမင်းက အရှင်မဟိန္ဒအား မေဃဝန်ဥယျာဉ်ကိုလှူပြီး မဟာဝိဟာရကျောင်းတိုက်ကြီးကို တည်ဆောက်ခဲ့သည်။ ရဟန်းတော် (၃၀၀၀) အမြဲသီတင်းသုံးခဲ့ပြီး သာသနာနှစ် ၄၄၀ ဝဋ္ဋဂါမဏိမင်းအထိ ဓမ္မဝိနယသာသနာတော်သည် အနှောင့်အယှက်ကင်းစွာ စည်ပင်ခဲ့သည်။' }
        },
        {
          date: { [LANGUAGES.EN]: 'Founding Philosophy', [LANGUAGES.MM]: 'တည်ထောင်မှုဆိုင်ရာ အတွေးအခေါ်' },
          title: { [LANGUAGES.EN]: 'A Modern University with Traditional Values', [LANGUAGES.MM]: 'ရှေးမူအတိုင်း ခေတ်မီတက္ကသိုလ်' },
          text: { [LANGUAGES.EN]: 'This university is a Buddhist institution founded with the guidance of revered elders, based on the philosophy of "old ways of living, modern ways of teaching." It is a place where monks who observe the Vinaya reside and study.', [LANGUAGES.MM]: 'ဤတက္ကသိုလ်သည် "နေနည်းရှေးကျ သင်နည်းခေတ်မီ" ဟူသော ရှေးမထေရ်မြတ်တို့၏ ဩဝါဒဖြင့် တည်ထောင်အပ်သော ဗုဒ္ဓတက္ကသိုလ်ဖြစ်ပါသည်။ ဝိနည်းဆောင် ရဟန်းတော်များ မွေ့ပျော်ရာ ဗုဒ္ဓတက္ကသိုလ်ဖြစ်ပါသည်။' }
        },
        {
          date: { [LANGUAGES.EN]: 'June 12, 2016', [LANGUAGES.MM]: 'ဇွန် ၁၂၊ ၂၀၁၆' },
          title: { [LANGUAGES.EN]: 'Official Opening', [LANGUAGES.MM]: 'တရားဝင်ဖွင့်ပွဲ' },
          text: { [LANGUAGES.EN]: 'The university was officially opened by senior monks led by the Chairman of the State Sangha Maha Nayaka Committee, Bhaddanta Kumara Bhivamsa (the Venerable Bhamo Sayadaw).', [LANGUAGES.MM]: 'နိုင်ငံတော်သံဃမဟာနာယကအဖွဲ့ဥက္ကဋ္ဌ ဘဒ္ဒန္တကုမာရာဘိဝံသ (ဗန်းမော်ဆရာတော်ကြီး) အမှူးပြုသော ဆရာတော်ကြီးများက ဖွင့်လှစ်တော်မူခဲ့ကြသည်။' }
        },
        {
          date: { [LANGUAGES.EN]: 'Present Day', [LANGUAGES.MM]: 'ယနေ့ခေတ်' },
          title: { [LANGUAGES.EN]: 'Current Academic Offerings', [LANGUAGES.MM]: 'လက်ရှိသင်ကြားရေးအစီအစဉ်များ' },
          text: { [LANGUAGES.EN]: 'This year, the university offers seven courses: the Pre-Diploma Course, Undergraduate Diploma Course, B.A. (Dhamma and Vinaya) Course, Postgraduate Diploma (Pali/Sanskrit) Course, M.A. (Dhamma - Vinaya) Course, M.A. (Pali/Sanskrit) Course, and the Takkasila Dhammasariya Course.', [LANGUAGES.MM]: 'ယခုနှစ်တွင် အကြိုဒီပလိုမာသင်တန်း၊ ဘွဲ့ကြိုဒီပလိုမာသင်တန်း၊ ဘီအေ(ဓမ္မဝိနယ) သင်တန်း၊ ဘွဲ့လွန် ဒီပလိုမာ (ပါဠိ/သက္ကတ) သင်တန်း၊ မဟာဝိဇ္ဇာ(ဓမ္မဝိနယ) သင်တန်း၊ မဟာဝိဇ္ဇာ(ပါဠိ/သက္က) သင်တန်း၊ တက္ကသီလဓမ္မာစရိယ သင်တန်း၊ စုစုပေါင်း သင်တန်း ၇ ခု ဖွင့်လှစ်လျက်ရှိပါသည်။' }
        }
      ]
    }
  };

  return (
    <div className="about-page">
      {/* Enhanced Banner Section */}
      <div className="about-hero-banner">
        <div className="about-hero-overlay"></div>
        <div className="about-hero-pattern"></div>
        <div className="container">
          <div className="about-hero-content">
            <div className="about-hero-icon">
              <div className="about-hero-lotus">
                <div className="lotus-petal"></div>
                <div className="lotus-petal"></div>
                <div className="lotus-petal"></div>
                <div className="lotus-petal"></div>
                <div className="lotus-petal"></div>
                <div className="lotus-petal"></div>
                <div className="lotus-petal"></div>
                <div className="lotus-petal"></div>
              </div>
            </div>
            <h1 className={`about-hero-title ${currentLanguage === LANGUAGES.MM ? 'myanmar-text' : ''}`}>
              {getText(aboutContent.banner.title)}
            </h1>
            <div className="about-hero-divider">
              <div className="divider-line"></div>
              <div className="divider-gem"></div>
              <div className="divider-line"></div>
            </div>
            <p className={`about-hero-subtitle ${currentLanguage === LANGUAGES.MM ? 'myanmar-text' : ''}`}>
              {getText(aboutContent.banner.subtitle)}
            </p>
            <div className="about-hero-scroll">
              <div className="scroll-indicator"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="about-content container section-padding">
        {/* Official Doctrine */}
        <section className="about-section">
          <div className="about-section__layout">
            <div className="about-section__image-wrapper">
              <div className="about-section__image-container">
                {/* === ADD YOUR IMAGE HERE === */}
                {<img src="/images/motto.png" alt="Official Doctrine" className="about-section__image" />}
                <div className="about-section__image-placeholder">
                  <FaRegImage size={48} />
                  <span>Image of the University Doctrine or Symbol</span>
                </div>
              </div>
            </div>
            <div className="about-section__text-wrapper">
              <h2 className={`about-section__title ${currentLanguage === LANGUAGES.MM ? 'myanmar-text' : ''}`}>
                {getText(aboutContent.officialDoctrine.title)}
              </h2>
              <div className={`about-section__text ${currentLanguage === LANGUAGES.MM ? 'myanmar-text' : ''}`}>
                {getText(aboutContent.officialDoctrine.content).split('\n').map((paragraph, index) => (
                  <p key={index} className="about-paragraph">
                    {paragraph.includes('"') ? (
                      <strong className="about-quote">{paragraph}</strong>
                    ) : (
                      paragraph
                    )}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Guiding Principle */}
        <section className="about-section about-section--highlight">
          <div className="about-section__layout about-section__layout--reversed">
            <div className="about-section__image-wrapper">
              <div className="about-section__image-container">
                {/* Updated image with the specified path */}
                <img
                  src="/images/doctrinee.jpg"
                  alt={currentLanguage === LANGUAGES.MM
                    ? "ရိုးရာနှင့် ခေတ်မီမှုကို ကိုယ်စားပြုသော ပုံ"
                    : "Image representing Tradition and Modernity"}
                  className="about-section__image about-section__image--tradition-modern"
                />
              </div>
            </div>
            <div className="about-section__text-wrapper">
              <h2 className={`about-section__title ${currentLanguage === LANGUAGES.MM ? 'myanmar-text' : ''}`}>
                {getText(aboutContent.guidingPrinciple.title)}
              </h2>
              <div className={`about-section__text ${currentLanguage === LANGUAGES.MM ? 'myanmar-text' : ''}`}>
                {getText(aboutContent.guidingPrinciple.content).split('\n').map((paragraph, index) => (
                  <p key={index} className="about-paragraph">
                    {paragraph.includes('အကျဉ်းချုပ်') || paragraph.includes('In summary') ? (
                      <span className="about-emphasis">{paragraph}</span>
                    ) : (
                      paragraph
                    )}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Motto */}
        <section className="about-section">
          <h2 className={`about-section__title about-section__title--centered ${currentLanguage === LANGUAGES.MM ? 'myanmar-text' : ''}`}>
            {getText(aboutContent.motto.title)}
          </h2>
          <div className={`about-section__text ${currentLanguage === LANGUAGES.MM ? 'myanmar-text' : ''}`}>
            {getText(aboutContent.motto.content).split('\n').map((paragraph, index) => (
              <p key={index} className="about-paragraph">
                {paragraph.includes('သီလပရိဓောတာ') || paragraph.includes('Sīlaparidhotā') ? (
                  <strong className="about-motto">{paragraph}</strong>
                ) : (
                  paragraph
                )}
              </p>
            ))}
          </div>
        </section>

        {/* Background History */}
        <section className="about-section about-section--history">
          <h2 className={`about-section__title about-section__title--centered ${currentLanguage === LANGUAGES.MM ? 'myanmar-text' : ''}`}>
            {getText(aboutContent.history.title)}
          </h2>
          <div className="history-timeline">
            {aboutContent.history.timeline.map((item, index) => (
              <div key={index} className="timeline-item">
                <div className="timeline-dot"></div>
                <div className={`timeline-date ${currentLanguage === LANGUAGES.MM ? 'myanmar-text' : ''}`}>
                  {getText(item.date)}
                </div>
                <div className="timeline-content">
                  <h3 className={`timeline-title ${currentLanguage === LANGUAGES.MM ? 'myanmar-text' : ''}`}>
                    {getText(item.title)}
                  </h3>
                  <p className={`${currentLanguage === LANGUAGES.MM ? 'myanmar-text' : ''}`}>
                    {getText(item.text)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

export default About;